import { Button, Input, Lock } from "@ews-admin/global-design-system";
import { zodResolver } from "@hookform/resolvers/zod";
import React, { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { toast } from "sonner";
import { z } from "zod";

import { accountApi } from "../services/account.api";
import { requestTerminateSessionRequest } from "../store/reducers/sessionSlice";

const changePasswordSchema = (t: (key: string) => string) =>
  z
    .object({
      oldPassword: z.string().min(1, t("common.validation.required")),
      newPassword: z
        .string()
        .min(8, t("common.validation.passwordTooShort"))
        .regex(
          /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/,
          t("common.validation.passwordComplexity")
        ),
      confirmPassword: z.string().min(1, t("common.validation.required")),
    })
    .refine((data) => data.newPassword === data.confirmPassword, {
      message: t("common.validation.passwordMismatch"),
      path: ["confirmPassword"],
    });

type ChangePasswordFormData = {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
};

export const ChangePassword: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<ChangePasswordFormData>({
    resolver: zodResolver(changePasswordSchema(t)),
  });

  const onSubmit = async (data: ChangePasswordFormData) => {
    setIsSubmitting(true);

    try {
      await accountApi.changePassword({
        oldPassword: data.oldPassword,
        newPassword: data.newPassword,
      });

      toast.success(t("changePassword.success"));

      // Clear session and redirect to login page - saga handles the redirect
      dispatch(requestTerminateSessionRequest());
    } catch (error) {
      console.error("Error changing password:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="flex justify-center items-center min-h-screen bg-gray-50">
      <div className="p-8 w-full max-w-md bg-white rounded-lg shadow-md">
        <div className="mb-6 text-center">
          <div className="flex justify-center items-center mx-auto mb-4 w-16 h-16 rounded-full bg-ews-primary/10">
            <Lock className="w-8 h-8 text-ews-primary" />
          </div>
          <h1 className="mb-2 text-2xl font-bold text-gray-900">
            {t("changePassword.title")}
          </h1>
          <p className="text-sm text-gray-600">
            {t("changePassword.description")}
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <Input
            {...register("oldPassword")}
            type="password"
            label={t("changePassword.oldPassword")}
            leftIcon={<Lock size={16} />}
            showPasswordToggle
            error={errors.oldPassword?.message}
            required
          />

          <Input
            {...register("newPassword")}
            type="password"
            label={t("changePassword.newPassword")}
            leftIcon={<Lock size={16} />}
            showPasswordToggle
            error={errors.newPassword?.message}
            required
          />
          <p className="text-xs text-gray-500">
            {t("changePassword.passwordRequirements")}
          </p>

          <Input
            {...register("confirmPassword")}
            type="password"
            label={t("changePassword.confirmPassword")}
            leftIcon={<Lock size={16} />}
            showPasswordToggle
            error={errors.confirmPassword?.message}
            required
          />

          <Button
            type="submit"
            loading={isSubmitting}
            disabled={isSubmitting}
            className="w-full"
          >
            {t("changePassword.submit")}
          </Button>
        </form>
      </div>
    </div>
  );
};
